import React, { useState, useRef, useEffect } from 'react';
import Mapa from './Mapa';
import RedesResponsive from './RedesResponsive';
import ReCAPTCHA from 'react-google-recaptcha';
import emailjs from '@emailjs/browser';
import { useTranslation } from 'react-i18next';

function ContactoForm() {
    const [t, i18n] = useTranslation("global");
    const [formData, setFormData] = useState({
        nombre: "",
        email: "",
        telefono: "",
        motivo: "",
    });
    const [showModal, setShowModal] = useState(false);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const recaptchaRef = useRef(); // Referencia al reCAPTCHA
    const formRef = useRef(); // Referencia al formulario
    const maxWords = 100;

    const Alert = ({ type, message, onClose }) => {
        useEffect(() => {
          const timer = setTimeout(() => {
            onClose();
          }, 5000); // Automatically close after 5 seconds
      
          return () => clearTimeout(timer);
        }, [onClose]);
      
        const typeStyles = {
          success: 'bg-green-500',
          error: 'bg-red-500'
        };
      
        return (
          <div className={`fixed top-4 right-4 z-50 p-4 text-white rounded ${typeStyles[type]}`}>
            {message}
            <button 
              onClick={onClose} 
              className="ml-4 text-white hover:opacity-75"
            >
              ✕
            </button>
          </div>
        );
      };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Validar límite de palabras en "motivo"
        if (name === "motivo") {
            const words = value.trim().split(/\s+/);
            if (words.length > maxWords) {
                setFormData({ ...formData, motivo: words.slice(0, maxWords).join(" ") });
                setError(true);
                return;
            } else {
                setError(false);
            }
        }

        setFormData({ ...formData, [name]: value });
    };

    const [alert, setAlert] = useState({
        show: false,
        type: '', // 'success' or 'error'
        message: ''
    });

    const sendEmail = async () => {
        setLoading(true);
        try {
            // Ejecutar reCAPTCHA y obtener el token
            const token = await recaptchaRef.current.executeAsync();
            recaptchaRef.current.reset();

            if (token) {
                // Enviar datos con EmailJS
                await emailjs.sendForm(
                    "service_uyu2unq", // Reemplaza con tu Service ID
                    "template_281gm9h", // Reemplaza con tu Template ID
                    formRef.current, // Referencia al formulario
                    "UxPKyHtfM6Ed0d_oy" // Reemplaza con tu Public Key
                );
                
                setFormData({
                  nombre: "",
                  email: "",
                  telefono: "",
                  motivo: "",
              });
                setLoading(false);
                setAlert({
                    show: true,
                    type: 'success',
                    message: t("contacto.envio-exitoso") // Add this translation
                  });
            } else {
                setLoading(false);
                setAlert({
                    show: true,
                    type: 'error',
                    message: t("contacto.error-recaptcha") // Add this translation
                  });
            }
            setShowModal(false);
        } catch (error) {
            console.error("Error al enviar el formulario:", error);
            setLoading(false);
            setAlert({
                show: true,
                type: 'error',
                message: t("contacto.error-envio") // Add this translation
              });
            setShowModal(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setShowModal(true); // Mostrar modal de confirmación
    };

    const confirmSubmit = async () => {
        await sendEmail(); // Enviar formulario
    };

    const Modal = ({ onConfirm, onCancel }) => (
        <div className="z-50 fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-footer z-20 p-8 rounded-lg text-white shadow-lg text-center w-11/12 md:w-1/3">
                <h2 className="text-2xl font-messina tracking-wide mb-4">{t("contacto.confirmar-titulo")}</h2>
                <p className="mb-6 text-xl">{t("contacto.confirmar-pregunta")}</p>
                <div className="flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        className="px-3 py-2 text-xl bg-gris-opaco text-white rounded-md"
                    >
                        {t("contacto.cancelar")}
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-3 py-2 text-xl bg-green-500 text-white rounded-md"
                    >{loading ? t("contacto.enviando") : t("contacto.confirmar")}</button>
                </div>
            </div>
        </div>
    );

    return (
        <div id="contacto" className="min-h-screen  w-full flex flex-col mx-auto py-10 px-4">
        <div className="w-full justify-start ">
            <h2 className="text-white w-full inline-block font-messina tracking-wide text-xl md:text-4xl text-start  mb-2">
            {t("contacto.title")}
            </h2>
        </div>
        <div className="h-1  w-20 bg-gradient-to-r from-gray-500 to-transparent mb-10"></div>

        <div className="w-full  flex flex-col lg:flex-row gap-8 justify-center items-stretch">
            {/* Formulario de contacto */}
            <div className="rounded-2xl w-full lg:w-1/2 bg-gradient-to-br from-zinc-900 to-zinc-800 border-2 border-zinc-600 p-10 shadow-2xl flex flex-col justify-between">
                <form
                    ref={formRef}
                    className="text-white flex flex-col w-full h-full font-kanit justify-between space-y-2"
                    onSubmit={handleSubmit}
                >   
                    <div className='flex-col flex'>
                        <label className=" font-extralight tracking-wide">{t("contacto.nombre")}</label>
                        <input
                            type="text"
                            name="nombre"
                            value={formData.nombre}
                            onChange={handleChange}
                            className="px-2 py-1 rounded-lg bg-zinc-700 border border-zinc-500 text-sm focus:outline-none focus:border-blue-400 transition-all"
                            required
                        />
                    </div>
                    <div className='flex-col flex'>
                        <label className=" font-extralight tracking-wide">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="px-2 py-1 rounded-lg bg-zinc-700 border border-zinc-500 text-sm focus:outline-none focus:border-blue-400 transition-all"
                            required
                        />
                    </div>
                    <div className='flex-col flex '>
                        <label className=" font-extralight tracking-wide">{t("contacto.telefono")}</label>
                        <input
                            type="text"
                            name="telefono"
                            value={formData.telefono}
                            onChange={handleChange}
                            className="px-2 py-1 rounded-lg bg-zinc-700 border border-zinc-500 text-sm focus:outline-none focus:border-blue-400 transition-all"
                        />
                    </div>
                    <div className='flex-col flex '>
                        <label className=" font-extralight tracking-wide">{t("contacto.motivo")}</label>
                        <textarea
                            name="motivo"
                            value={formData.motivo}
                            onChange={handleChange}
                            rows="4"
                            className="px-2 py-1 rounded-lg bg-zinc-700 border border-zinc-500 text-sm focus:outline-none focus:border-blue-400 placeholder:font-extralight transition-all resize-none"
                            placeholder={`${t("contacto.palabras")} ${maxWords}`}
                        ></textarea>
                    </div>
                    {error && <p className="text-red-400 text-base">{t("contacto.error")}</p>}
                    <button
                        type="submit"
                        className="font-messina tracking-wider bg-zinc-600 hover:from-zinc-600 hover:to-zinc-800 text-base md:text-lg px-6 py-2 rounded-lg  shadow-lg transition-all transform hover:scale-105"
                    >
                        {t("contacto.enviar")}
                    </button>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey="6Lc3wosqAAAAAO66SUuwqZG47532dN2yhZ6zg4u5"
                        size="invisible"
                        theme="dark"
                        className="hidden"
                    />
                </form>
                {showModal && <Modal onConfirm={confirmSubmit} onCancel={() => setShowModal(false)} />}
            </div>

            {alert.show && (
                <Alert 
                    type={alert.type} 
                    message={alert.message} 
                    onClose={() => setAlert({ ...alert, show: false })} 
                />
            )}

            {/* Mapa */}
            <div className="rounded-2xl w-full lg:w-1/2 bg-gradient-to-br from-zinc-900 to-zinc-800 border-2 border-zinc-600 p-10 shadow-2xl flex flex-col">
                <h2 className="text-white  text-lg md:text-xl  font-messina tracking-wide mb-3">
                    {t("contacto.encontrar")}
                </h2>
                <p className="text-sm md:text-base font-extralight  text-gray-300 mb-4">Diagonal 76 Nº 46 entre 14 y 15, La Plata, Buenos Aires</p>
                <div className="rounded-xl shadow-2xl flex-1 lg:min-h-[300px]">
                    <Mapa />
                </div>
            </div>
        </div>
        <RedesResponsive />
    </div>
    );
}

export default ContactoForm;